import {AfterContentChecked, ChangeDetectorRef, Component, forwardRef, Input, OnInit, ViewChild} from "@angular/core";
import {AbstractVisbosControl} from "../../../core/visbos-core/ui/forms/abstract-visbos-control";
import {ProcurementService} from "../../services/procurement-service/procurement.service";
import {Order} from "../../../shared/view-model/store-management/order.type";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {appInjector} from "../../../index";
import {OrderItem} from "../../../shared/view-model/store-management/order-item.type";
import {ProductBatchInput} from "../../model/product-batch-input.type";
import {ProductBatchFormComponent} from "../../product/product-batch-form/product-batch-form.component";
import {SessionActions} from "../../../shared/actions/session.actions";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store/store";
import {ApolloQueryObservable} from "apollo-angular";
import {Observable} from "rxjs/Observable";
import {ErrorHandlerService} from "../../../core/visbos-core/error-handling/error-handler.service";
import {ActivatedRoute, Router} from "@angular/router";
import {DeliveryItemsListComponent} from "../delivery-items-list/delivery-items-list.component";
import {MessageType} from "../../../shared/redux-store/session/session.types";
import {Location} from "@angular/common";
import {MedicineAuthFormComponent} from "../../withdrawal/medicine-auth-form/medicine-auth-form.component";
import {map} from "rxjs/operators";
import {LoadOrderQueryQuery} from '../../../shared/graphql/graphql.types';

@Component({
  selector: 'vb-new-delivery',
  templateUrl: './new-delivery.component.html',
  styleUrls: ['./new-delivery.component.css'],
  providers: [{provide: AbstractVisbosControl, useExisting: forwardRef(() => NewDeliveryComponent)}]
})
export class NewDeliveryComponent extends AbstractVisbosControl<Order, FormGroup> implements OnInit, AfterContentChecked {

  @Input()
  orderCuid: string | Observable<string>;

  @ViewChild(DeliveryItemsListComponent)
  deliverItemsCmp: DeliveryItemsListComponent;

  hasItems: boolean = false;
  selectedDeliveryItems = [];

  searchTerm: FormControl = new FormControl();

  private order$: ApolloQueryObservable<LoadOrderQueryQuery>;

  get order(): Order {
    return this.item;
  }

  set order(value: Order) {
    this.item = value;
  }

  medicineAuthForm: FormGroup;

  constructor(fb: FormBuilder, cd: ChangeDetectorRef,
              private router: Router, private route: ActivatedRoute, private loc: Location,
              sessionActions: SessionActions, private ngRx: NgRedux<IAppState>, private err: ErrorHandlerService,
              private procurementService: ProcurementService) {
    super(fb, cd, sessionActions);

    this.actions = [
      {
        label: "Ware einlagern",
        icon: "fa-paper-plane",
        action: () => this.sendDelivery.apply(this),
        disabled: () => !this.canSend.apply(this)
      }
    ]

  }

  ngOnInit() {
    super.ngOnInit();

    if (!this.orderCuid) {
      this.orderCuid = this.route.params.pipe(
        map(params => params.orderCuid)
      );
    }

    this.order$ = this.procurementService.loadOrder(this.orderCuid);
    this.addComponentData(this.order$);

    this.dataSubscriptions.push(
      this.order$.subscribe(res => {
          this.order = res.data.order;
          this.patchItemValues();
        },
        err => {
          this.err.handleUserError(err, "Fehler beim Laden der Bestellung!");
        }));
  }

  ngAfterContentChecked(): void {
    this.cd.detectChanges();
  }

  buildControlContent(values?: Order): FormGroup {
    return NewDeliveryComponent.initControl(this.fb);
  }

  public static initControl(fb: FormBuilder = null): FormGroup {
    fb = fb ? fb : appInjector().get(FormBuilder);

    return fb.group({
      cuid: [null],
      delivery_date: [null, Validators.required],
      packing_slip_number: [null],
      recipient_name: [null],
      supplier_id: [null, Validators.required],
      order_id: [null, Validators.required],
      administration_unit_id: [null, Validators.required],
      user_id: [null],
      delivery_items: fb.array([], Validators.required)
    });
  }

  public setDefaultValues(item?: Order): { [controlKey: string]: any } | any {

    if (!item) {
      return undefined;
    }

    let val = {
      cuid: AbstractVisbosControl.cuid(),
      delivery_date: new Date().toISOString().slice(0, 10),
      packing_slip_number: null,
      recipient_name: this.ngRx.getState().session.user.label,
      supplier_id: this.setValue(item, 'supplier.cuid'),
      order_id: this.setValue(item, 'cuid'),
      administration_unit_id: this.setValue(item, 'administration_unit.cuid'),
      user_id: this.ngRx.getState().session.user.cuid
    };

    this.addDeliveryItems(item, <FormArray>this.control.get('delivery_items'));

    return val;

  }

  private canSend(): boolean {
    let authOk = !this.medicineAuthForm || (this.medicineAuthForm && this.medicineAuthForm.valid);
    return this.hasItems && authOk;
  }

  createMedicineAuthComponent() {
    this.medicineAuthForm = MedicineAuthFormComponent.initControl(this.fb);
    this.cd.detectChanges();
  }

  addDeliveryItems(order: Order, formArray: FormArray) {
    formArray.controls = [];
    let orderItems: OrderItem[] = order.order_items;

    orderItems.forEach(item => {
      if (item.amount_in_pieces_open > 0) {
        formArray.push(this.initDeliveryItemControl(order, item));
        if (!this.medicineAuthForm && item.available_stocks.find(st => st.registration_required === true)) {
          this.createMedicineAuthComponent();
        }
      }

    });

  }

  initDeliveryItemControl(order?: Order, values?: OrderItem): FormGroup {

    return this.fb.group({
      cuid: AbstractVisbosControl.cuid(),
      article_number: [this.setValue(values, 'article_number')],
      article_label: [this.setValue(values, 'article_label')],
      packing_unit: [this.setValue(values, 'packing_unit')],
      packing_content: [this.setValue(values, 'packing_content'), Validators.required],
      amount: [this.setValue(values, 'amount_in_orig_packing_open'), Validators.required],
      price: [this.setValue(values, 'price')],
      product_packing_id: [this.setValue(values, 'product_packing.cuid'), Validators.required],
      referenced_order_item_id: [this.setValue(values, 'cuid')],
      stock_id: [null, Validators.required],
      product_batches: this.fb.array([])
    });

  }

  getOrderItem(deliveryItem): OrderItem {
    return this.order.order_items.find(item => item.cuid === deliveryItem.referenced_order_item_id);
  }


  addProductBatch(itemIndex: number, productBatchInput?: ProductBatchInput) {
    let batchArrayCtrl: FormArray = this.control.get(`product_batches.${itemIndex}.product_batches`) as FormArray;

    let batchCtrl = ProductBatchFormComponent.initControl(this.fb);
    batchCtrl.patchValue(productBatchInput);

    batchArrayCtrl.push(batchCtrl)
  }

  removeProductBatch(itemIndex: number, productBatchIndex: number) {
    let batchArrayCtrl: FormArray = this.control.get(`product_batches.${itemIndex}.product_batches`) as FormArray;
    batchArrayCtrl.removeAt(productBatchIndex);
  }

  onSelectedChanged(selected: any[]) {
    this.hasItems = (selected && selected.length > 0);
    this.selectedDeliveryItems = selected;
  }


  sendDelivery() {
    let input = Object.assign({}, this.control.value);
    let medAuthData = this.medicineAuthForm ? this.medicineAuthForm.value : null;

    input.delivery_items = input.delivery_items.filter(item => this.selectedDeliveryItems.find(sel => sel === item.cuid));

    this.procurementService.createDelivery(this.order.cuid, input, medAuthData).toPromise()
      .then(res => {
        this.sessionActions.pushMessage("Lieferung erfolgreich angelegt und Waren eingelagert!", MessageType.SUCCESS, "Warenlieferung");
        this.router.navigate(['/client/lagerverwaltung/bestellungen/offen']);
      })
      .catch(err => {
        this.err.handleUserError(err, "Fehler beim Anlegen der Lieferung und Einlagern der Waren!")
      });

  }


}



// WEBPACK FOOTER //
// ./src/app/store-management/procurement/new-delivery/new-delivery.component.ts